/**
 * Selection State Management Utilities
 * 
 * Handles selection logic for tree hierarchy including:
 * - Parent/child cascade selection
 * - Select all functionality
 * - Partial selection states
 */

import { TreeGridRow } from '../types';
import { getDescendantIds, getAncestorIds } from './treeOperations';

/**
 * Toggle row selection with cascade to children
 */
export function toggleRowSelection(
  rowId: string,
  selectedIds: Set<string>,
  rowsById: Map<string, TreeGridRow>,
  cascadeToChildren: boolean = true
): Set<string> {
  const newSelection = new Set(selectedIds);
  const isCurrentlySelected = selectedIds.has(rowId);
  
  if (isCurrentlySelected) {
    // Deselect row and all descendants
    newSelection.delete(rowId);
    if (cascadeToChildren) {
      const descendants = getDescendantIds(rowId, rowsById);
      descendants.forEach(id => newSelection.delete(id));
    }
  } else {
    // Select row and all descendants
    newSelection.add(rowId);
    if (cascadeToChildren) {
      const descendants = getDescendantIds(rowId, rowsById);
      descendants.forEach(id => newSelection.add(id));
    }
  }
  
  // Update parent states
  updateParentSelectionStates(rowId, newSelection, rowsById);
  
  return newSelection;
}

/**
 * Update parent selection states based on children
 */
function updateParentSelectionStates(
  rowId: string,
  selectedIds: Set<string>,
  rowsById: Map<string, TreeGridRow>
): void {
  const ancestors = getAncestorIds(rowId, rowsById);
  
  ancestors.forEach(ancestorId => {
    const ancestor = rowsById.get(ancestorId);
    if (!ancestor) return;
    
    const allChildrenSelected = ancestor.childrenIds.every(childId =>
      selectedIds.has(childId)
    );
    
    if (allChildrenSelected && ancestor.childrenIds.length > 0) {
      selectedIds.add(ancestorId);
    } else {
      selectedIds.delete(ancestorId);
    }
  });
}

/**
 * Select all rows
 */
export function selectAll(rows: TreeGridRow[]): Set<string> {
  return new Set(rows.map(row => row.id));
}

/**
 * Deselect all rows
 */
export function deselectAll(): Set<string> {
  return new Set();
}

/**
 * Get selection state for a row
 */
export function getSelectionState(
  rowId: string,
  selectedIds: Set<string>,
  rowsById: Map<string, TreeGridRow>
): 'selected' | 'partial' | 'unselected' {
  const row = rowsById.get(rowId);
  if (!row) return 'unselected';
  
  if (selectedIds.has(rowId)) {
    return 'selected';
  }
  
  // Check if any children are selected (partial state)
  if (row.childrenIds.length > 0) {
    const someChildrenSelected = row.childrenIds.some(childId =>
      selectedIds.has(childId) ||
      getSelectionState(childId, selectedIds, rowsById) !== 'unselected'
    );
    
    if (someChildrenSelected) {
      return 'partial';
    }
  }
  
  return 'unselected';
}

/**
 * Get all selected rows (excluding descendants of selected parents)
 */
export function getTopLevelSelectedRows(
  selectedIds: Set<string>,
  rowsById: Map<string, TreeGridRow>
): string[] {
  const result: string[] = [];
  
  selectedIds.forEach(rowId => {
    const row = rowsById.get(rowId);
    if (!row) return;
    
    // Check if any ancestor is selected
    const ancestors = getAncestorIds(rowId, rowsById);
    const hasSelectedAncestor = ancestors.some(ancestorId =>
      selectedIds.has(ancestorId)
    );
    
    if (!hasSelectedAncestor) {
      result.push(rowId);
    }
  });
  
  return result;
}

/**
 * Get all selected rows including descendants
 */
export function getAllSelectedRows(
  selectedIds: Set<string>,
  rowsById: Map<string, TreeGridRow>
): string[] {
  const result = new Set<string>();
  
  selectedIds.forEach(rowId => {
    result.add(rowId);
    const descendants = getDescendantIds(rowId, rowsById);
    descendants.forEach(id => result.add(id));
  });
  
  return Array.from(result);
}

/**
 * Check if all visible rows are selected
 */
export function areAllVisibleRowsSelected(
  visibleRows: TreeGridRow[],
  selectedIds: Set<string>
): boolean {
  if (visibleRows.length === 0) return false;
  return visibleRows.every(row => selectedIds.has(row.id));
}

/**
 * Check if some (but not all) visible rows are selected
 */
export function areSomeVisibleRowsSelected(
  visibleRows: TreeGridRow[],
  selectedIds: Set<string>
): boolean {
  if (visibleRows.length === 0) return false;
  const selectedCount = visibleRows.filter(row => selectedIds.has(row.id)).length;
  return selectedCount > 0 && selectedCount < visibleRows.length;
}

/**
 * Select visible rows only (respecting current expansion state)
 */
export function selectVisibleRows(
  visibleRows: TreeGridRow[]
): Set<string> {
  return new Set(visibleRows.map(row => row.id));
}

/**
 * Remove deleted rows from selection
 */
export function removeFromSelection(
  rowIds: string[],
  selectedIds: Set<string>
): Set<string> {
  const newSelection = new Set(selectedIds);
  rowIds.forEach(id => newSelection.delete(id));
  return newSelection;
}

/**
 * Handle selection after row move
 */
export function handleSelectionAfterMove(
  movedRowId: string,
  selectedIds: Set<string>,
  rowsById: Map<string, TreeGridRow>,
  maintainSelection: boolean = true
): Set<string> {
  if (!maintainSelection) {
    return removeFromSelection([movedRowId], selectedIds);
  }
  
  // Maintain selection but update parent states
  const newSelection = new Set(selectedIds);
  updateParentSelectionStates(movedRowId, newSelection, rowsById);
  
  return newSelection;
}
